import { useState } from 'react';
import { complianceApi } from '@/api/compliance.api';

interface HsnVerifyModalProps {
  onClose: () => void;
  initialHsn?: string;
}

function isAuthError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('token') || m.includes('authenticat') || m.includes('credential');
}

export default function HsnVerifyModal({ onClose, initialHsn = '' }: HsnVerifyModalProps) {
  const [hsn, setHsn] = useState(initialHsn.trim());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ hsnCode: string; description: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [authNote, setAuthNote] = useState<string | null>(null);

  const handleVerify = async () => {
    const clean = hsn.trim().replace(/\D/g, '');
    if (![4, 6, 8].includes(clean.length)) {
      setError('HSN code must be exactly 4, 6 or 8 digits');
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);
    setAuthNote(null);
    try {
      const res = await complianceApi.verifyHsn(clean);
      if (res?.success && res.data) {
        setResult(res.data);
      } else {
        setError(res?.message || 'HSN code could not be verified');
      }
    } catch (err: any) {
      setError(err?.message || 'HSN code could not be verified');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    setAuthenticating(true);
    setAuthNote(null);
    try {
      const res = await complianceApi.testEWayBillAuth({});
      if (res?.success) {
        setAuthNote(res.message || 'Authenticated with Whitebooks ✓ — try Verify again.');
        setError(null);
      } else {
        setAuthNote(res?.message || 'Authentication failed');
      }
    } catch (err: any) {
      setAuthNote(err?.message || 'Authentication failed');
    } finally {
      setAuthenticating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in-95 duration-150 border border-slate-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="w-10 h-10 rounded-full bg-indigo-50 text-[#4f46e5] flex items-center justify-center mb-3">
          <i className="ri-barcode-box-line text-xl" />
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-0.5">Verify HSN Code</h3>
        <p className="text-xs text-gray-500 mb-4">
          Check an HSN/SAC code against the government's master, directly via NIC — before it breaks e-Way Bill or e-Invoice generation.
        </p>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={hsn}
            onChange={(e) => { setHsn(e.target.value.replace(/\D/g, '')); setError(null); }}
            onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
            maxLength={8}
            placeholder="e.g. 847130"
            className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <button
            onClick={handleVerify}
            disabled={loading || ![4, 6, 8].includes(hsn.trim().length)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-line" />}
            Verify
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 space-y-2">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
            {isAuthError(error) && (
              <button
                onClick={handleAuthenticate}
                disabled={authenticating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {authenticating ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-shield-keyhole-line" />}
                Authenticate with Whitebooks
              </button>
            )}
          </div>
        )}

        {authNote && (
          <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700 mt-2">
            <i className="ri-information-line text-sm shrink-0 mt-0.5" />
            <span>{authNote}</span>
          </div>
        )}

        {result && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                <i className="ri-check-line text-xs" />
              </span>
              <span className="font-mono font-bold text-emerald-900">{result.hsnCode}</span>
              <span className="ml-auto px-2 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-100 text-emerald-700 rounded">
                Valid
              </span>
            </div>
            {result.description && (
              <p className="text-xs text-emerald-800 leading-relaxed">{result.description}</p>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-4 w-full py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
        >
          Close
        </button>
      </div>
    </div>
  );
}
